/**
 * xAPI (Tin Can) Client for Corporate Training Integration
 *
 * Sends learning experience statements to a Learning Record Store (LRS)
 */

// xAPI Verb definitions
export const XAPI_VERBS = {
  initialized: {
    id: 'http://adlnet.gov/expapi/verbs/initialized',
    display: { 'en-US': 'initialized' }
  },
  answered: {
    id: 'http://adlnet.gov/expapi/verbs/answered',
    display: { 'en-US': 'answered' }
  },
  completed: {
    id: 'http://adlnet.gov/expapi/verbs/completed',
    display: { 'en-US': 'completed' }
  },
  passed: {
    id: 'http://adlnet.gov/expapi/verbs/passed',
    display: { 'en-US': 'passed' }
  },
  failed: {
    id: 'http://adlnet.gov/expapi/verbs/failed',
    display: { 'en-US': 'failed' }
  },
  experienced: {
    id: 'http://adlnet.gov/expapi/verbs/experienced',
    display: { 'en-US': 'experienced' }
  },
  attempted: {
    id: 'http://adlnet.gov/expapi/verbs/attempted',
    display: { 'en-US': 'attempted' }
  },
  progressed: {
    id: 'http://adlnet.gov/expapi/verbs/progressed',
    display: { 'en-US': 'progressed' }
  }
} as const;

// xAPI Activity Types
export const XAPI_ACTIVITY_TYPES = {
  assessment: 'http://adlnet.gov/expapi/activities/assessment',
  question: 'http://adlnet.gov/expapi/activities/question',
  course: 'http://adlnet.gov/expapi/activities/course',
  module: 'http://adlnet.gov/expapi/activities/module',
  interaction: 'http://adlnet.gov/expapi/activities/interaction'
} as const;

// Types
export interface XAPIConfig {
  enabled: boolean;
  endpoint: string;
  username: string;
  password: string;
  organizationId?: string;
  organizationName?: string;
}

export interface XAPIActor {
  objectType: 'Agent';
  name?: string;
  mbox?: string;
  account?: {
    homePage: string;
    name: string;
  };
}

export interface XAPIVerb {
  id: string;
  display: Record<string, string>;
}

export interface XAPIObject {
  id: string;
  objectType: 'Activity';
  definition?: {
    type?: string;
    name?: Record<string, string>;
    description?: Record<string, string>;
    extensions?: Record<string, unknown>;
    interactionType?: string;
    correctResponsesPattern?: string[];
    choices?: Array<{ id: string; description: Record<string, string> }>;
  };
}

export interface XAPIResult {
  score?: {
    scaled?: number;
    raw?: number;
    min?: number;
    max?: number;
  };
  success?: boolean;
  completion?: boolean;
  response?: string;
  duration?: string;
  extensions?: Record<string, unknown>;
}

export interface XAPIContext {
  registration?: string;
  instructor?: XAPIActor;
  team?: { objectType: 'Group'; name: string };
  contextActivities?: {
    parent?: XAPIObject[];
    grouping?: XAPIObject[];
    category?: XAPIObject[];
  };
  extensions?: Record<string, unknown>;
}

export interface XAPIStatement {
  id?: string;
  actor: XAPIActor;
  verb: XAPIVerb;
  object: XAPIObject;
  result?: XAPIResult;
  context?: XAPIContext;
  timestamp?: string;
}

// Convert duration in milliseconds to ISO 8601 duration format
export function msToISO8601Duration(ms: number): string {
  const seconds = Math.floor(ms / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);

  const remainingSeconds = seconds % 60;
  const remainingMinutes = minutes % 60;

  let duration = 'PT';
  if (hours > 0) duration += `${hours}H`;
  if (remainingMinutes > 0) duration += `${remainingMinutes}M`;
  duration += `${remainingSeconds}S`;

  return duration;
}

// Generate a UUID for statement IDs
function generateUUID(): string {
  return crypto.randomUUID();
}

// xAPI Client class
export class XAPIClient {
  private config: XAPIConfig;
  private baseActivityId: string;
  private registrationId: string;

  constructor(config: XAPIConfig, baseActivityId: string = 'https://balanced-arena.com') {
    this.config = config;
    this.baseActivityId = baseActivityId;
    this.registrationId = generateUUID();
  }

  // Check if xAPI is enabled and configured
  isEnabled(): boolean {
    return this.config.enabled && !!this.config.endpoint && !!this.config.username;
  }

  // Create an actor from user info
  createActor(userId: string, name?: string, email?: string): XAPIActor {
    const actor: XAPIActor = {
      objectType: 'Agent',
      name: name,
    };

    if (email) {
      actor.mbox = `mailto:${email}`;
    } else {
      actor.account = {
        homePage: this.baseActivityId,
        name: userId
      };
    }

    return actor;
  }

  // Send a statement to the LRS
  async sendStatement(statement: XAPIStatement): Promise<boolean> {
    if (!this.isEnabled()) {
      console.log('xAPI not enabled, skipping statement');
      return false;
    }

    // Add statement ID and timestamp if not present
    if (!statement.id) {
      statement.id = generateUUID();
    }
    if (!statement.timestamp) {
      statement.timestamp = new Date().toISOString();
    }

    // Add registration to context
    if (!statement.context) {
      statement.context = {};
    }
    statement.context.registration = this.registrationId;

    // Add organization info if configured
    if (this.config.organizationId) {
      statement.context.extensions = {
        ...statement.context.extensions,
        'https://balanced-arena.com/extensions/organizationId': this.config.organizationId,
        'https://balanced-arena.com/extensions/organizationName': this.config.organizationName,
      };
    }

    try {
      const response = await fetch(`${this.config.endpoint}/statements`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': 'Basic ' + btoa(`${this.config.username}:${this.config.password}`),
          'X-Experience-API-Version': '1.0.3'
        },
        body: JSON.stringify(statement)
      });

      if (!response.ok) {
        console.error('xAPI statement failed:', response.status, await response.text());
        return false;
      }

      return true;
    } catch (error) {
      console.error('xAPI send error:', error);
      return false;
    }
  }

  // Send multiple statements
  async sendStatements(statements: XAPIStatement[]): Promise<boolean> {
    if (!this.isEnabled()) return false;

    const enrichedStatements = statements.map(s => ({
      ...s,
      id: s.id || generateUUID(),
      timestamp: s.timestamp || new Date().toISOString(),
      context: {
        ...s.context,
        registration: this.registrationId
      }
    }));

    try {
      const response = await fetch(`${this.config.endpoint}/statements`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': 'Basic ' + btoa(`${this.config.username}:${this.config.password}`),
          'X-Experience-API-Version': '1.0.3'
        },
        body: JSON.stringify(enrichedStatements)
      });

      return response.ok;
    } catch (error) {
      console.error('xAPI batch send error:', error);
      return false;
    }
  }

  // Helper: Quiz started statement
  quizStarted(actor: XAPIActor, quizId: string, quizName: string, category?: string): XAPIStatement {
    return {
      actor,
      verb: XAPI_VERBS.initialized,
      object: {
        id: `${this.baseActivityId}/quiz/${quizId}`,
        objectType: 'Activity',
        definition: {
          type: XAPI_ACTIVITY_TYPES.assessment,
          name: { 'en-US': quizName },
          description: { 'en-US': `Quiz: ${quizName}` },
          extensions: category ? {
            'https://balanced-arena.com/extensions/category': category
          } : undefined
        }
      }
    };
  }

  // Helper: Question answered statement
  questionAnswered(
    actor: XAPIActor,
    questionId: string,
    questionText: string,
    response: string,
    correct: boolean,
    durationMs: number,
    options?: {
      category?: string;
      subcategory?: string;
      difficulty?: number;
      correctAnswer?: string;
      choices?: string[];
    }
  ): XAPIStatement {
    const result: XAPIResult = {
      success: correct,
      response: response,
      duration: msToISO8601Duration(durationMs),
      score: {
        scaled: correct ? 1 : 0,
        raw: correct ? 1 : 0,
        min: 0,
        max: 1
      }
    };

    const definition: XAPIObject['definition'] = {
      type: XAPI_ACTIVITY_TYPES.question,
      name: { 'en-US': questionText.slice(0, 100) },
      description: { 'en-US': questionText },
      interactionType: 'choice',
    };

    if (options?.correctAnswer) {
      definition.correctResponsesPattern = [options.correctAnswer];
    }

    if (options?.choices) {
      definition.choices = options.choices.map((c, i) => ({
        id: String(i),
        description: { 'en-US': c }
      }));
    }

    if (options?.category || options?.difficulty !== undefined) {
      definition.extensions = {
        ...(options.category && { 'https://balanced-arena.com/extensions/category': options.category }),
        ...(options.subcategory && { 'https://balanced-arena.com/extensions/subcategory': options.subcategory }),
        ...(options.difficulty !== undefined && { 'https://balanced-arena.com/extensions/difficulty': options.difficulty }),
      };
    }

    return {
      actor,
      verb: XAPI_VERBS.answered,
      object: {
        id: `${this.baseActivityId}/question/${questionId}`,
        objectType: 'Activity',
        definition
      },
      result
    };
  }

  // Helper: Quiz completed statement
  quizCompleted(
    actor: XAPIActor,
    quizId: string,
    quizName: string,
    score: number,
    maxScore: number,
    durationMs: number,
    passed: boolean,
    category?: string
  ): XAPIStatement {
    return {
      actor,
      verb: passed ? XAPI_VERBS.passed : XAPI_VERBS.failed,
      object: {
        id: `${this.baseActivityId}/quiz/${quizId}`,
        objectType: 'Activity',
        definition: {
          type: XAPI_ACTIVITY_TYPES.assessment,
          name: { 'en-US': quizName },
          extensions: category ? {
            'https://balanced-arena.com/extensions/category': category
          } : undefined
        }
      },
      result: {
        score: {
          scaled: maxScore > 0 ? score / maxScore : 0,
          raw: score,
          min: 0,
          max: maxScore
        },
        success: passed,
        completion: true,
        duration: msToISO8601Duration(durationMs)
      }
    };
  }

  // Helper: Progress statement
  quizProgress(
    actor: XAPIActor,
    quizId: string,
    quizName: string,
    currentQuestion: number,
    totalQuestions: number
  ): XAPIStatement {
    return {
      actor,
      verb: XAPI_VERBS.progressed,
      object: {
        id: `${this.baseActivityId}/quiz/${quizId}`,
        objectType: 'Activity',
        definition: {
          type: XAPI_ACTIVITY_TYPES.assessment,
          name: { 'en-US': quizName }
        }
      },
      result: {
        extensions: {
          'https://balanced-arena.com/extensions/currentQuestion': currentQuestion,
          'https://balanced-arena.com/extensions/totalQuestions': totalQuestions,
          'https://balanced-arena.com/extensions/progress': currentQuestion / totalQuestions
        }
      }
    };
  }
}

// Default config (disabled)
export const DEFAULT_XAPI_CONFIG: XAPIConfig = {
  enabled: false,
  endpoint: '',
  username: '',
  password: '',
  organizationId: '',
  organizationName: ''
};

// Storage key for xAPI config
const XAPI_CONFIG_KEY = 'balanced-arena-xapi-config';

// Load xAPI config from localStorage
export function loadXAPIConfig(): XAPIConfig {
  try {
    const stored = localStorage.getItem(XAPI_CONFIG_KEY);
    if (stored) {
      return { ...DEFAULT_XAPI_CONFIG, ...JSON.parse(stored) };
    }
  } catch (e) {
    console.error('Failed to load xAPI config:', e);
  }
  return DEFAULT_XAPI_CONFIG;
}

// Save xAPI config to localStorage
export function saveXAPIConfig(config: XAPIConfig): void {
  try {
    localStorage.setItem(XAPI_CONFIG_KEY, JSON.stringify(config));
  } catch (e) {
    console.error('Failed to save xAPI config:', e);
  }
}

// Create a client instance with stored config
export function createXAPIClient(): XAPIClient {
  const config = loadXAPIConfig();
  return new XAPIClient(config);
}
